import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	IAffaire,
	IAffairesList,
	IArchiveAffairesRequest,
	IAutorisationPrelevementRequestDto,
	IDocumentDownloadOtt,
	IReleveOperationsRequestDto,
	ISearchAffaireFilter,
	IUnarchiveAffairesRequest
} from '@core-lib/models';
import { environment } from '@environments/environment';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { Observable, tap } from 'rxjs';
import { AuthSharedService } from './auth.service';

@Injectable({
	providedIn: 'root'
})
export class AffaireSharedService {
	today = this.convertDate(dayjs(new Date()));

	private convertDate(inputFormat) {
		function pad(s) {
			return s < 10 ? '0' + s : s;
		}
		const d = new Date(inputFormat);
		return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear() + ' ' + pad(d.getHours()) + 'H' + pad(d.getMinutes());
	}

	get tenantKey() {
		return this.authService.currentTenantKey;
	}

	constructor(
		private http: HttpClient,
		private authService: AuthSharedService
	) {}

	getOperationSeuilId(affaireId: string): Observable<string> {
		return this.http.get<string>(`${environment.apiUrl}/t/${this.tenantKey}/affaire/${affaireId}/operationSeuil`);
	}

	getOperationReleveOtt(request: IReleveOperationsRequestDto): Observable<{ ott?: string; hasEmptyAffaires: boolean }> {
		return this.http.post<{ ott?: string; hasEmptyAffaires: boolean }>(
			`${environment.apiUrl}/t/${this.tenantKey}/affaireEdition/operation/releve/ott`,
			request
		);
	}

	getOperationRelevePdf(ott: string): string {
		return `${environment.apiUrl}/t/${this.tenantKey}/affaireEdition/operation/releve/pdf/${ott}`;
	}

	unarchiveAffaires(request: IUnarchiveAffairesRequest): Observable<string[]> {
		return this.http.post<string[]>(`${environment.apiUrl}/t/${this.tenantKey}/affaire/DesarchiverAffaires`, request);
	}

	archiveAffaires(request: IArchiveAffairesRequest): Observable<any> {
		return this.http.post<string[]>(`${environment.apiUrl}/t/${this.tenantKey}/affaire/ArchiverAffaires`, request);
	}

	exportUnarchiveAffaires(filterModel: IUnarchiveAffairesRequest): Observable<Blob | ArrayBuffer> {
		return this.http
			.post<Blob>(`${environment.apiUrl}/t/${this.tenantKey}/affaireEdition/ExportUnarchiveAffaires`, filterModel, {
				responseType: 'blob' as 'json'
			})
			.pipe(
				tap(data => {
					const blob = new Blob([data as unknown as BlobPart], { type: 'text/csv' });
					const fileName = 'Export Affaires désarchivées au ' + this.today + '.csv';
					saveAs(blob, fileName);
				})
			);
	}

	getAffairesJumellesPourVE(affaireId: string): Observable<IAffaire[]> {
		return this.http.get<IAffaire[]>(`${environment.apiUrl}/t/${this.tenantKey}/affaire/${affaireId}/AffairesJumellesPourVE`);
	}

	searchAffaire(searchAffaireForm: ISearchAffaireFilter): Observable<IAffairesList> {
		const response = this.http.post<IAffairesList>(`${environment.apiUrl}/t/${this.tenantKey}/Affaire/search`, searchAffaireForm);
		return response;
	}

	getAutorisationPrelevementOtt(request: IAutorisationPrelevementRequestDto): Observable<IDocumentDownloadOtt> {
		return this.http.post<IDocumentDownloadOtt>(
			`${environment.apiUrl}/t/${this.tenantKey}/affaireEdition/affaire/AutorisationPrelevement/ott`,
			request
		);
	}

	getAutorisationPrelevementPdfFile(ott: string): string {
		return `${environment.apiUrl}/t/${this.tenantKey}/affaireEdition/affaire/AutorisationPrelevement/pdf/${ott}`;
	}
}
